import { useEffect, useState } from "react";
import EmailSequenceService from "../Services/EmailSequenceService";
import UtilsService from "../Services/UtilsService";
import { Tooltip } from 'react-tooltip';
import moment from "moment";
import ContentLoader from 'react-content-loader';
import { FaList, FaClock, FaPaperPlane, FaTimesCircle, FaReply } from 'react-icons/fa';
import AdminEmailOffCanvas from '../Offcanvas/AdminEmailOffCanvas';

function AdminEmails() {
    const [emailSequences, setEmailSequences] = useState([]);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const PAGE_SIZE = 200;
    const [statusFilter, setStatusFilter] = useState('all');
    const [showOffCanvas, setShowOffCanvas] = useState(false);
    const [selectedEmail, setSelectedEmail] = useState(null);

    useEffect(() => {
        getEmailSequences(1, statusFilter);
        // eslint-disable-next-line
    }, [statusFilter]);

    async function getEmailSequences(pageParam = 1, statusParam = statusFilter) {
        setLoading(true);
        let { data, total } = await EmailSequenceService.getAdminEmailSequences(pageParam, statusParam);
        setEmailSequences(data);
        setTotal(total);
        setPage(pageParam);
        setLoading(false);
    }

    function handleOpenOffCanvas(emailSequence) {
        setSelectedEmail(emailSequence);
        setShowOffCanvas(true);
    }
    function handleCloseOffCanvas() {
        setShowOffCanvas(false);
        setSelectedEmail(null);
    }

    function renderStepButtons(steps) {
        return steps.map((step, i) => {
            let className = 'btn btn-outline-primary btn-sm btn-circle mx-1';
            let tooltipContent = `Email not sent yet, estimated to be sent on ${moment(step.estimated_send_at).format('MM/DD/YYYY hh:mm a')}`;

            if (step.email_log && step.status === 'sent' && step.email_log.sent_result === 'email sent') {
                className = 'btn btn-outline-success btn-sm btn-circle mx-1';
                tooltipContent = `Email successfully sent at ${moment(step.email_log.sent_at).format('MM/DD/YYYY hh:mm a')}`;
            } else if (step.email_log && step.email_log.sent_result !== 'email sent') {
                className = 'btn btn-outline-danger btn-sm btn-circle mx-1';
                tooltipContent = `Email failed with error ${step.email_log.sent_result} at ${moment(step.email_log.sent_at).format('MM/DD/YYYY hh:mm a')}`;
            } else if (step.status === 'replied') {
                className = 'btn btn-outline-info btn-sm btn-circle mx-1';
                tooltipContent = `Email replied at ${moment(step.replied_at).format('MM/DD/YYYY hh:mm a')}`;
            } else if (step.status === 'bounced') {
                className = 'btn btn-outline-info btn-sm btn-circle mx-1';
                tooltipContent = `Email not sent, reason: bounced at ${moment(step.bounced_at).format('MM/DD/YYYY hh:mm a')}`;
            } else if (step.status === 'error') {
                className = 'btn btn-outline-danger btn-sm btn-circle mx-1';
                tooltipContent = `Email not sent, reason: ${step.error} at ${moment(step.error_at).format('MM/DD/YYYY hh:mm a')}`;
            } else {
                className = 'btn btn-outline-warning btn-sm btn-circle mx-1';
            }

            return <button data-tooltip-id="admin-emails-tooltip" data-tooltip-content={tooltipContent} className={className}>S{i + 1}</button>;
        });
    }

    function filterEmailSequences() {
        let sequences = emailSequences;
       
        if (search) {
            sequences = sequences.filter(seq => {
                let text = `${seq.lead.email} ${seq.lead.name} ${seq.sequence.name}`.toLowerCase();
                return text.includes(search.toLowerCase());
            });
        }
        return sequences;
    }

    function renderEmailSequences() {
        let sequences = filterEmailSequences();
        return sequences.map((emailSequence, i) => {
            let stepIndex = emailSequence.current_step - 1;
            let step = emailSequence.steps[stepIndex] || emailSequence.steps[emailSequence.steps.length - 1];

            return (
                <div key={`email-sequence-${i}`} className='email-sequence-item'>
                    <span className="fs-13" style={{ minWidth: '160px', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', marginRight: 16 }}>
                        <div className='text-muted small'>From:</div>
                        <span className='text-primary bold'>{emailSequence.user?.name}</span>
                        <div className='text-muted small'>{emailSequence.user?.email}</div>
                    </span>
                    <span className="fs-13" style={{ minWidth: '180px', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        onClick={() => handleOpenOffCanvas(emailSequence)}
                        role="button"
                    >
                        To <span className='text-primary bold'>{emailSequence.lead?.name}</span>
                        <div className='text-muted small'>{emailSequence.lead?.email}</div>
                        <div className='text-muted small'>Sequence: {emailSequence?.sequence?.name}</div>
                    </span>
                    <div className="fs-12" style={{ flex: 1 }} onClick={() => handleOpenOffCanvas(emailSequence)} role="button">
                        <div className="bold mb-1">
                            {UtilsService.replaceTextWithUserAndLeadVariables(step.sequence_step.subject, emailSequence.user, emailSequence.lead)}
                        </div>
                        <div>{UtilsService.replaceTextWithUserAndLeadVariables(step.sequence_step?.textBody?.substring(0, 300), emailSequence.user, emailSequence.lead)}...</div>
                    </div>
                    <div className="d-flex">
                        {renderStepButtons(emailSequence.steps)}
                    </div>
                </div>
            );
        });
    }

    function renderPagination() {
        const totalPages = Math.ceil(total / PAGE_SIZE);
        if (totalPages <= 1) return null;

        const pageButtons = [];
        // Mostrar máximo 7 botones: actual, 3 antes y 3 después
        let start = Math.max(1, page - 3);
        let end = Math.min(totalPages, page + 3);
        if (end - start < 6) {
            if (start === 1) {
                end = Math.min(totalPages, start + 6);
            } else if (end === totalPages) {
                start = Math.max(1, end - 6);
            }
        }
        for (let i = start; i <= end; i++) {
            pageButtons.push(
                <button
                    key={i}
                    className={`btn btn-sm mx-1 ${i === page ? 'btn-primary' : 'btn-secondary'}`}
                    disabled={i === page}
                    onClick={() => getEmailSequences(i, statusFilter)}
                >
                    {i}
                </button>
            );
        }
        return (
            <div className="ulysses-search-pagination justify-content-center my-3">
                <button
                    className="btn btn-sm btn-secondary mx-1"
                    onClick={() => getEmailSequences(page - 1, statusFilter)}
                    disabled={page === 1}
                >
                    &lt;
                </button>
                {start > 1 && <span className="mx-1">...</span>}
                {pageButtons}
                {end < totalPages && <span className="mx-1">...</span>}
                <button
                    className="btn btn-sm btn-secondary mx-1"
                    onClick={() => getEmailSequences(page + 1, statusFilter)}
                    disabled={page === totalPages}
                >
                    &gt;
                </button>
            </div>
        );
    }

    function renderSkeletonLoader() {
        // Simula 5 filas de email-sequence-item
        return (
            <div className="my-3">
                {[...Array(5)].map((_, idx) => (
                    <ContentLoader
                        key={idx}
                        speed={2}
                        width="100%"
                        height={70}
                        viewBox="0 0 800 70"
                        backgroundColor="#f3f3f3"
                        foregroundColor="#ecebeb"
                        style={{ width: '100%', maxWidth: 900 }}
                    >
                        {/* Simula avatar/círculo */}
                        <rect x="10" y="15" rx="6" ry="6" width="40" height="40" />
                        {/* Simula nombre y email */}
                        <rect x="60" y="18" rx="4" ry="4" width="140" height="12" />
                        <rect x="60" y="36" rx="3" ry="3" width="120" height="10" />
                        {/* Simula subject */}
                        <rect x="220" y="18" rx="4" ry="4" width="300" height="14" />
                        {/* Simula body */}
                        <rect x="220" y="38" rx="3" ry="3" width="400" height="10" />
                        {/* Simula botones de pasos */}
                        <rect x="650" y="25" rx="16" ry="16" width="32" height="32" />
                        <rect x="690" y="25" rx="16" ry="16" width="32" height="32" />
                        <rect x="730" y="25" rx="16" ry="16" width="32" height="32" />
                    </ContentLoader>
                ))}
            </div>
        );
    }

    return (
        <div>
            <div className="container">
                <div className="sequence-detail-container">
                    <div className='d-flex mb-2'>
                        <input type="text"
                            value={search}
                            className="input-search"
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="search by email or user" />
                        <div className="d-flex ms-3 filter-btn-group">
                            <div
                                className={`filter-btn pointer ${statusFilter === 'all' ? 'active' : ''}`}
                                onClick={() => { if (!loading) { setStatusFilter('all'); getEmailSequences(1, 'all'); } }}
                                style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}
                            >
                                <FaList className="me-1" /> <span>All</span>
                            </div>
                            <div
                                className={`filter-btn pointer ${statusFilter === 'pending' ? 'active' : ''}`}
                                onClick={() => { if (!loading) { setStatusFilter('pending'); getEmailSequences(1, 'pending'); } }}
                                style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}
                            >
                                <FaClock className="me-1" /> <span>Pending</span>
                            </div>
                            <div
                                className={`filter-btn pointer ${statusFilter === 'sent' ? 'active' : ''}`}
                                onClick={() => { if (!loading) { setStatusFilter('sent'); getEmailSequences(1, 'sent'); } }}
                                style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}
                            >
                                <FaPaperPlane className="me-1" /> <span>1st email sent</span>
                            </div>
                            <div
                                className={`filter-btn pointer ${statusFilter === 'sentStep2' ? 'active' : ''}`}
                                onClick={() => { if (!loading) { setStatusFilter('sentStep2'); getEmailSequences(1, 'sentStep2'); } }}
                                style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}
                            >
                                <FaPaperPlane className="me-1" style={{ color: '#6c63ff' }} /> <span>2nd email sent</span>
                            </div>
                            <div
                                className={`filter-btn pointer ${statusFilter === 'bounced' ? 'active' : ''}`}
                                onClick={() => { if (!loading) { setStatusFilter('bounced'); getEmailSequences(1, 'bounced'); } }}
                                style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}
                            >
                                <FaTimesCircle className="me-1" style={{ color: '#e74c3c' }} /> <span>Bounced</span>
                            </div>
                            <div
                                className={`filter-btn pointer ${statusFilter === 'replied' ? 'active' : ''}`}
                                onClick={() => { if (!loading) { setStatusFilter('replied'); getEmailSequences(1, 'replied'); } }}
                                style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}
                            >
                                <FaReply className="me-1" style={{ color: '#27ae60' }} /> <span>Replied</span>
                            </div>
                        </div>
                    </div>
                    {renderPagination()}
                    <div className="fs-13 text-muted mb-2" style={{marginLeft: 4}}>
                        {total} {total === 1 ? 'result found' : 'results found'}
                    </div>
                    {loading && renderSkeletonLoader()}
                    {!loading && renderEmailSequences()}
                    {renderPagination()}
                </div>
            </div>
            <Tooltip id="admin-emails-tooltip" />
            <AdminEmailOffCanvas show={showOffCanvas} onClose={handleCloseOffCanvas} emailData={selectedEmail} />
        </div>
    );
}

export default AdminEmails;
